import React from 'react';

/* Store WhatsApp, same number the backend sends payment instructions from. */
const WHATSAPP_LINK = 'https://wa.me/601151214897';

const VALUES = [
    {
        icon: (
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
        title: 'A short range, on purpose',
        desc: 'We would rather stock a handful of tapes and bandages we understand properly than list hundreds we cannot speak for. If it is on the site, we can tell you what it is for.',
        gradient: 'from-blue-500 to-blue-600',
    },
    {
        icon: (
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
        ),
        title: 'Guides, free and open',
        desc: 'Our taping guides are on the site for anyone to read — no account, no email, no purchase. If they help you tape something yourself with tape you already own, that is fine by us.',
        gradient: 'from-indigo-500 to-indigo-600',
    },
    {
        icon: (
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.8L3 21l1.4-3.5A7.9 7.9 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
        ),
        title: 'You can reach us',
        desc: 'Orders and questions go through the same WhatsApp number, answered by the same people who pack the boxes. No ticket queue, no call centre script.',
        gradient: 'from-violet-500 to-violet-600',
    },
];

/* Plain facts a first-time customer can verify, in place of the
   invented head-count and satisfaction-rate figures. */
const ESSENTIALS = [
    { label: 'Registered as', value: 'TN MEDICA ENTERPRISE' },
    { label: 'Ships from', value: 'Malaysia, nationwide' },
    { label: 'Delivery', value: 'Free, no minimum spend' },
    { label: 'Payment', value: 'Bank transfer or DuitNow QR' },
];

const AboutPage = () => {
    return (
        <div className="overflow-x-hidden">
            {/* ── HERO ─────────────────────────────────────────── */}
            <section
                className="relative min-h-[55vh] flex items-center justify-center overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #0a0a14 0%, #0f172a 50%, #1e1b4b 100%)' }}
            >
                <div className="absolute top-1/2 left-1/4 w-96 h-96 rounded-full opacity-20 pointer-events-none"
                    style={{ background: 'radial-gradient(circle, #3b82f6 0%, transparent 70%)', transform: 'translateY(-50%)' }} />
                <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
                    style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

                <div className="relative z-10 text-center px-6 pt-28 pb-20">
                    <div className="inline-flex items-center gap-2 border border-blue-500/40 rounded-full px-5 py-2 text-blue-300 text-xs font-medium tracking-widest mb-8"
                        style={{ background: 'rgba(59,130,246,0.12)', backdropFilter: 'blur(8px)' }}>
                        <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                        OUR STORY
                    </div>
                    <h1 className="text-6xl sm:text-7xl font-bold text-white mb-6 tracking-tight">About Doctape</h1>
                    <p className="text-xl text-slate-300 font-light max-w-2xl mx-auto leading-relaxed">
                        A small Malaysian business supplying sports tape, bandages and pitchside kit —
                        and giving the taping guides away for free.
                    </p>
                </div>

                <div className="absolute bottom-0 left-0 right-0">
                    <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
                        <path d="M0 60L60 50C120 40 240 20 360 15C480 10 600 20 720 25C840 30 960 30 1080 25C1200 20 1320 10 1380 5L1440 0V60H0Z" fill="white" />
                    </svg>
                </div>
            </section>

            {/* ── STORY ────────────────────────────────────────── */}
            <section className="py-24 bg-white">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <p className="text-xs font-bold tracking-widest text-blue-600 mb-4 uppercase">Our Story</p>
                            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 leading-tight">
                                We would rather<br />
                                <span style={{ background: 'linear-gradient(135deg, #2563eb, #6366f1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                                    earn it than claim it
                                </span>
                            </h2>
                            <div className="space-y-5 text-slate-600 font-light leading-relaxed">
                                <p>Doctape is new. We are not going to tell you that thousands of clinics already rely on us, because they do not yet — we have only just opened, and you may well be among the first people to order from us.</p>
                                <p>What we can tell you is what we do. We supply rigid tape, kinesiology tape, elastic adhesive and cohesive bandages, underwrap and pitchside kit, chosen because they are the things physios, trainers and athletes go through week after week. We keep the range short so that we can answer questions about all of it.</p>
                                <p>Delivery is free anywhere in Malaysia with no minimum, payment is by bank transfer or DuitNow after you order, and every order gets a tracking number. If any of that goes wrong, you are dealing with us directly rather than a support desk.</p>
                            </div>
                        </div>

                        {/* The essentials */}
                        <div className="bg-slate-50 border border-slate-100 rounded-3xl p-8 sm:p-10">
                            <p className="text-xs font-bold tracking-widest text-blue-600 mb-6 uppercase">The essentials</p>
                            <dl className="space-y-5">
                                {ESSENTIALS.map((e, i) => (
                                    <div key={i} className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 pb-5 border-b border-slate-200 last:border-0 last:pb-0">
                                        <dt className="text-xs text-slate-400 font-medium uppercase tracking-widest">{e.label}</dt>
                                        <dd className="text-slate-900 font-semibold sm:text-right">{e.value}</dd>
                                    </div>
                                ))}
                            </dl>
                            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer"
                                className="inline-flex items-center gap-2 mt-8 text-blue-600 hover:text-blue-700 font-semibold text-sm transition-colors duration-300">
                                Ask us anything on WhatsApp
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Wave */}
            <div className="bg-white">
                <svg viewBox="0 0 1440 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block">
                    <path d="M0 0L120 8C240 16 480 32 720 34.7C960 37.3 1200 26.7 1320 21.3L1440 16V50H0Z" fill="#f8fafc" />
                </svg>
            </div>

            {/* ── VALUES ───────────────────────────────────────── */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <p className="text-xs font-bold tracking-widest text-blue-600 mb-3 uppercase">How we work</p>
                        <h2 className="text-4xl md:text-5xl font-bold text-slate-900">Three things we stick to</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {VALUES.map((v, i) => (
                            <div key={i} className="group bg-white border border-slate-100 hover:border-blue-200 rounded-3xl p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-blue-100 text-center">
                                <div className={`w-16 h-16 rounded-3xl bg-gradient-to-br ${v.gradient} flex items-center justify-center text-white mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    {v.icon}
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-4">{v.title}</h3>
                                <p className="text-slate-500 font-light leading-relaxed text-sm">{v.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Wave into dark */}
            <div className="bg-slate-50">
                <svg viewBox="0 0 1440 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block">
                    <path d="M0 50L120 40C240 30 480 10 720 5C960 0 1200 10 1320 18L1440 26V0H0Z" fill="#0f172a" />
                </svg>
            </div>

            {/* ── CTA ──────────────────────────────────────────── */}
            <section className="py-24" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)' }}>
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Have a look around</h2>
                    <p className="text-slate-300 font-light text-lg mb-10 leading-relaxed">The full range is short enough to read in a few minutes, and the taping guides are free whether you buy anything or not.</p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a href="/catalog"
                            className="inline-flex items-center justify-center gap-2 text-white font-semibold px-9 py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5"
                            style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)', boxShadow: '0 8px 32px rgba(59,130,246,0.35)' }}>
                            Browse Products →
                        </a>
                        <a href="/training-guides"
                            className="inline-flex items-center justify-center gap-2 text-slate-300 hover:text-white font-medium px-9 py-4 rounded-full border border-white/15 hover:border-white/35 transition-all duration-300"
                            style={{ backdropFilter: 'blur(8px)', background: 'rgba(255,255,255,0.04)' }}>
                            View Guides
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AboutPage;
